import React from 'react';

const C = {
  bg:'#08090f', card:'#0d1117', inp:'#0f1420',
  border:'#1f2937', borderS:'#1a2035',
  primary:'#e5e7eb', secondary:'#6b7280', muted:'#374151',
  profit:'#16C784', loss:'#ef4444', accent:'#6366f1',
};

function SDiv({ label }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8, margin:'4px 0' }}>
      <div style={{ flex:1, height:1, background:C.borderS }}/>
      <span style={{ color:C.muted, fontSize:9, fontWeight:800, letterSpacing:2, textTransform:'uppercase' }}>{label}</span>
      <div style={{ flex:1, height:1, background:C.borderS }}/>
    </div>
  );
}

function SCard({ children, accent, style={} }) {
  return (
    <div style={{
      borderRadius:12, padding:'10px 12px', boxSizing:'border-box',
      background: accent ? `linear-gradient(135deg,${accent}12 0%,${C.card} 100%)` : C.card,
      border:`1px solid ${accent ? `${accent}28` : C.border}`, ...style,
    }}>
      {children}
    </div>
  );
}

function SHead({ emoji, title, color }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:8 }}>
      <div style={{ width:20, height:20, borderRadius:6, background:`${color||C.accent}22`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, fontSize:11 }}>
        {emoji}
      </div>
      <span style={{ color:C.primary, fontSize:11, fontWeight:800, textTransform:'uppercase', letterSpacing:1 }}>{title}</span>
    </div>
  );
}

function MC({ label, value, color }) {
  return (
    <div style={{ padding:'5px 8px', borderRadius:8, background:color?`${color}10`:C.inp, border:`1px solid ${color?`${color}22`:C.border}` }}>
      <div style={{ color:C.secondary, fontSize:8, fontWeight:600, marginBottom:1 }}>{label}</div>
      <div style={{ color:color||C.primary, fontSize:11, fontWeight:800, lineHeight:1.2 }}>{value??'—'}</div>
    </div>
  );
}

function Pill({ label, color }) {
  return (
    <span style={{ display:'inline-block', background:`${color}18`, color, border:`1px solid ${color}28`, borderRadius:20, padding:'2px 8px', fontSize:9, fontWeight:700, marginRight:3, marginBottom:3 }}>
      {label}
    </span>
  );
}

function BacktestShareCard({ results, cardRef }) {
  if (!results) return null;

  const symbol    = (results.symbol||'').replace('.NS','')||results.name||'Strategy';
  const returnPct = parseFloat(results.total_return_pct??0);
  const winRate   = parseFloat(results.win_rate_pct??0);
  const maxDD     = parseFloat(results.max_drawdown_pct??0);
  const numTrades = results.num_trades??0;
  const numWin    = results.num_winning_trades??0;
  const numLoss   = results.num_losing_trades??0;
  const sharpe    = results.sharpe_ratio!=null?parseFloat(results.sharpe_ratio).toFixed(2):null;
  const pf        = results.profit_factor!=null?parseFloat(results.profit_factor).toFixed(2):null;
  const cagr      = results.cagr_pct!=null?parseFloat(results.cagr_pct).toFixed(1):null;
  const avgHold   = results.avg_holding_days!=null?Math.round(results.avg_holding_days):null;
  const benchmark = results.benchmark_return_pct!=null?parseFloat(results.benchmark_return_pct).toFixed(1):null;
  const capInit   = results.capital_initial!=null?parseFloat(results.capital_initial):null;
  const capFinal  = results.capital_final!=null?parseFloat(results.capital_final):null;
  const commission= results.total_commission!=null?parseFloat(results.total_commission):null;
  const trades    = results.trades||[];
  const mode      = results.mode||'visual';
  const modeLabel = mode==='visual'?'Visual':mode==='code'?'Code':'AI';
  const modeColor = mode==='visual'?C.accent:mode==='code'?'#f59e0b':'#8b5cf6';
  const startDate = results.start_date?new Date(results.start_date).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'}):'';
  const endDate   = results.end_date?new Date(results.end_date).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'}):'';
  const isPos     = returnPct>=0;
  const ACC       = isPos?C.profit:C.loss;
  const winBarPct = numTrades>0?Math.round((numWin/numTrades)*100):0;
  const inr       = v=>v!=null?`₹${Math.abs(Math.round(Number(v))).toLocaleString('en-IN')}`:'—';

  const cfg = results.config||{};
  const conditions = cfg.conditions||[];
  const entryLogic = cfg.entry_logic||'ALL';
  const slPct = cfg.stop_loss_pct??cfg.sl_pct??null;
  const tpPct = cfg.target_pct??cfg.tp_pct??null;
  const COND_LABELS = { rsi_oversold:'RSI<35', rsi_overbought:'RSI>65', macd_bullish:'MACD Bull', macd_bearish:'MACD Bear', above_sma50:'Above SMA50', below_sma50:'Below SMA50', supertrend_bullish:'ST Bull', supertrend_bearish:'ST Bear', high_volume:'High Vol', adx_trending:'ADX Trend', golden_cross:'Golden ✕', death_cross:'Death ✕', bollinger_squeeze:'BB Squeeze', price_breakout:'Breakout' };
  const condLabels = conditions.map(c=>COND_LABELS[c]||c);
  const showLogic = mode==='visual'&&condLabels.length>0;

  const yearMap = {};
  for (const t of trades) {
    const yr = (t.entry_date||'').slice(0,4)||'?';
    if (!yearMap[yr]) yearMap[yr]={trades:0,wins:0,pnl:0};
    yearMap[yr].trades++; if((t.pnl||0)>0)yearMap[yr].wins++; yearMap[yr].pnl+=t.pnl||0;
  }
  const years = Object.keys(yearMap).sort();
  const showYearly = years.length>=2;

  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const monthly = results.monthly_returns||[];
  const mYears = [...new Set(monthly.map(m=>m.year))].sort();
  const mLookup = {};
  monthly.forEach(m=>{mLookup[`${m.year}-${m.month}`]=m.pnl;});
  const maxAbsPnl = Math.max(...monthly.map(m=>Math.abs(m.pnl||0)),1);
  const showMonthly = monthly.length>0;

  const outcomeMap = {};
  for (const t of trades) {
    const r = t.exit_reason||'OTHER';
    if (!outcomeMap[r]) outcomeMap[r]={count:0,pnl:0};
    outcomeMap[r].count++; outcomeMap[r].pnl+=t.pnl||0;
  }
  const outcomeRows = Object.entries(outcomeMap).sort((a,b)=>b[1].count-a[1].count);
  const showOutcomes = outcomeRows.length>0&&trades.length>=2;
  const labelMap = { TP:'Target Hit', SL:'Stop Loss', SIGNAL:'Signal Exit', END:'End of Period' };
  const colorMap = { TP:C.profit, SL:C.loss, SIGNAL:C.accent, END:C.secondary };

  return (
    <div
      ref={cardRef}
      style={{
        width:1080, height:1350, background:C.bg, overflow:'hidden', position:'relative',
        fontFamily:"'Segoe UI',system-ui,-apple-system,sans-serif",
        display:'flex', flexDirection:'column', padding:28, gap:7, boxSizing:'border-box',
      }}
    >
      {/* Ambient glow */}
      <div style={{ position:'absolute', top:-80, right:-80, width:200, height:200, borderRadius:'50%', background:`radial-gradient(circle,${ACC}0e 0%,transparent 65%)`, pointerEvents:'none' }}/>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:4, flexShrink:0 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <div style={{ width:28, height:28, borderRadius:8, background:'linear-gradient(135deg,#22c55e30,#22c55e10)', border:'1px solid #22c55e35', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <span style={{ color:'#22c55e', fontSize:14, fontWeight:900 }}>T</span>
          </div>
          <span style={{ color:'#fff', fontSize:16, fontWeight:800 }}>TradeReflex</span>
        </div>
        <span style={{ color:C.muted, fontSize:11 }}>{startDate}{endDate?` → ${endDate}`:''}</span>
        <span style={{ color:C.muted, fontSize:11 }}>tradereflex.in</span>
      </div>

      {/* Hero */}
      <SCard accent={ACC}>
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:8 }}>
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:4, flexWrap:'wrap' }}>
              <span style={{ color:'#fff', fontSize:28, fontWeight:900, letterSpacing:'-0.5px', lineHeight:1 }}>{symbol}</span>
              <Pill label={modeLabel} color={modeColor}/>
              <Pill label={isPos?'PROFIT':'LOSS'} color={ACC}/>
            </div>
            <p style={{ color:ACC, fontSize:34, fontWeight:900, margin:0, lineHeight:1, letterSpacing:'-1px' }}>{isPos?'+':''}{returnPct.toFixed(2)}%</p>
            <p style={{ color:ACC, fontSize:12, fontWeight:700, margin:'3px 0 0', opacity:0.8 }}>Total Return</p>
          </div>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:6 }}>
          <MC label="Win Rate" value={`${winRate.toFixed(1)}%`} color={winRate>=50?C.profit:'#f59e0b'}/>
          {cagr && <MC label="CAGR" value={`${parseFloat(cagr)>=0?'+':''}${cagr}%`} color={parseFloat(cagr)>=0?C.profit:C.loss}/>}
          <MC label="Max DD" value={`−${Math.abs(maxDD).toFixed(1)}%`} color={C.loss}/>
          {pf && <MC label="Profit Factor" value={`${pf}x`} color={parseFloat(pf)>=1.5?C.profit:parseFloat(pf)>=1?'#f59e0b':C.loss}/>}
          {sharpe && <MC label="Sharpe" value={sharpe} color={C.accent}/>}
          {avgHold && <MC label="Avg Hold" value={`${avgHold}d`}/>}
          {benchmark!=null && <MC label="vs B&H" value={`${parseFloat(benchmark)>=0?'+':''}${benchmark}%`} color={parseFloat(benchmark)>=0?C.profit:C.loss}/>}
          <MC label="Trades" value={numTrades}/>
        </div>
      </SCard>

      {/* Win/Loss bar */}
      {numTrades>0 && (
        <SCard>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:5 }}>
            <span style={{ color:C.profit, fontSize:11, fontWeight:700 }}>↑ {numWin} Wins ({winBarPct}%)</span>
            <span style={{ color:C.loss, fontSize:11, fontWeight:700 }}>{numLoss} Losses ↓</span>
          </div>
          <div style={{ height:6, borderRadius:3, background:C.border, overflow:'hidden' }}>
            <div style={{ width:`${winBarPct}%`, height:'100%', background:`linear-gradient(90deg,${C.profit},#22d3ee)`, borderRadius:3 }}/>
          </div>
        </SCard>
      )}

      {/* 2-column: Entry Logic + Yearly */}
      {(showLogic || showYearly) && (
        <div style={{ display:'flex', gap:7 }}>
          {showLogic && (
            <SCard accent={modeColor} style={{ flex:1 }}>
              <SHead emoji="🧠" title="Entry Conditions" color={modeColor}/>
              <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginBottom:condLabels.length&&(slPct!=null||tpPct!=null)?6:0 }}>
                {condLabels.map((cond,i) => <Pill key={i} label={cond} color={C.profit}/>)}
                <Pill label={`${entryLogic} match`} color={modeColor}/>
              </div>
              {(slPct!=null||tpPct!=null) && (
                <div style={{ display:'flex', gap:5 }}>
                  {slPct!=null && <Pill label={`SL ${slPct}%`} color={C.loss}/>}
                  {tpPct!=null && <Pill label={`Target ${tpPct}%`} color={C.profit}/>}
                </div>
              )}
            </SCard>
          )}

          {showYearly && (
            <SCard style={{ flex:1 }}>
              <SHead emoji="📅" title="Annual P&L" color={C.accent}/>
              <div style={{ borderTop:`1px solid ${C.border}` }}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr 1fr', padding:'3px 0', marginBottom:3 }}>
                  {['Year','Trades','Wins','Win%','P&L'].map(h => <span key={h} style={{ color:C.secondary, fontSize:9, fontWeight:700 }}>{h}</span>)}
                </div>
                {years.map(yr => {
                  const m = yearMap[yr]; const wp = m.trades>0?Math.round((m.wins/m.trades)*100):0;
                  return (
                    <div key={yr} style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr 1fr', padding:'4px 0', borderTop:`1px solid ${C.border}` }}>
                      <span style={{ color:C.primary, fontSize:10, fontWeight:700 }}>{yr}</span>
                      <span style={{ color:C.secondary, fontSize:10 }}>{m.trades}</span>
                      <span style={{ color:C.secondary, fontSize:10 }}>{m.wins}</span>
                      <span style={{ color:wp>=50?C.profit:C.loss, fontSize:10, fontWeight:700 }}>{wp}%</span>
                      <span style={{ color:m.pnl>=0?C.profit:C.loss, fontSize:10, fontWeight:700 }}>{m.pnl>=0?'+':''}₹{Math.abs(Math.round(m.pnl)).toLocaleString('en-IN')}</span>
                    </div>
                  );
                })}
              </div>
            </SCard>
          )}
        </div>
      )}

      {/* Monthly heatmap */}
      {showMonthly && (
        <SCard>
          <SHead emoji="🗓" title="Monthly Heatmap" color="#f59e0b"/>
          <div>
            <div style={{ display:'grid', gridTemplateColumns:'36px repeat(12,1fr)', gap:2, marginBottom:4 }}>
              <span/>
              {MONTHS.map(m => <span key={m} style={{ color:C.secondary, fontSize:8, fontWeight:600, textAlign:'center' }}>{m}</span>)}
            </div>
            {mYears.map(yr => (
              <div key={yr} style={{ display:'grid', gridTemplateColumns:'36px repeat(12,1fr)', gap:2, marginBottom:2 }}>
                <span style={{ color:C.secondary, fontSize:10, fontWeight:700, display:'flex', alignItems:'center' }}>{yr}</span>
                {MONTHS.map((_,mi) => {
                  const pnl = mLookup[`${yr}-${mi+1}`]??null;
                  const intensity = pnl!=null?Math.min(Math.abs(pnl)/maxAbsPnl,1):0;
                  const bg = pnl==null?'transparent':pnl>0?`rgba(22,199,132,${0.1+intensity*0.5})`:`rgba(239,68,68,${0.1+intensity*0.5})`;
                  const col = pnl==null?'#2a3040':pnl>=0?C.profit:C.loss;
                  const fmt = pnl==null?'·':Math.abs(pnl)>=1000?`${pnl>=0?'+':'−'}${(Math.abs(pnl)/1000).toFixed(0)}k`:`${pnl>=0?'+':'−'}${Math.abs(Math.round(pnl))}`;
                  return <div key={mi} style={{ backgroundColor:bg, color:col, fontSize:8, fontWeight:700, textAlign:'center', padding:'3px 1px', borderRadius:3 }}>{fmt}</div>;
                })}
              </div>
            ))}
          </div>
        </SCard>
      )}

      {/* 2-column: Exit Outcomes + Bottom metrics */}
      <div style={{ display:'flex', gap:7 }}>
        {showOutcomes && (
          <SCard style={{ flex:2 }}>
            <SHead emoji="🚪" title="Exit Breakdown" color="#06b6d4"/>
            <div style={{ borderTop:`1px solid ${C.border}` }}>
              {outcomeRows.map(([reason,m],i) => {
                const col = colorMap[reason]||C.secondary;
                const share = numTrades>0?Math.round((m.count/numTrades)*100):0;
                const avg = m.count>0?m.pnl/m.count:0;
                return (
                  <div key={reason} style={{ display:'grid', gridTemplateColumns:'1fr 40px 40px 1fr 1fr', padding:'4px 0', borderTop:i>0?`1px solid ${C.border}`:'none', alignItems:'center' }}>
                    <span style={{ background:`${col}18`, color:col, borderRadius:6, padding:'2px 6px', fontSize:9, fontWeight:800, display:'inline-block' }}>{labelMap[reason]||reason}</span>
                    <span style={{ color:C.secondary, fontSize:9, textAlign:'right' }}>{m.count}</span>
                    <span style={{ color:C.secondary, fontSize:9, textAlign:'right' }}>{share}%</span>
                    <span style={{ color:m.pnl>=0?C.profit:C.loss, fontSize:9, fontWeight:700, textAlign:'right' }}>{m.pnl>=0?'+':''}₹{Math.abs(Math.round(m.pnl)).toLocaleString('en-IN')}</span>
                    <span style={{ color:avg>=0?C.profit:C.loss, fontSize:9, textAlign:'right' }}>{avg>=0?'+':''}₹{Math.abs(Math.round(avg)).toLocaleString('en-IN')}/t</span>
                  </div>
                );
              })}
            </div>
          </SCard>
        )}

        <div style={{ flex:1, display:'flex', flexDirection:'column', gap:7 }}>
          {capInit   && <MC label="Capital In"  value={inr(capInit)}  color={C.secondary}/>}
          {capFinal  && <MC label="Capital Out" value={inr(capFinal)} color={capFinal>=capInit?C.profit:C.loss}/>}
          {commission!=null && <MC label="Commission" value={inr(commission)} color="#f59e0b"/>}
          {avgHold   && <MC label="Avg Hold"   value={`${avgHold}d`}/>}
        </div>
      </div>

      {/* Footer */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', paddingTop:8, borderTop:`1px solid ${C.borderS}`, flexShrink:0, marginTop:'auto' }}>
        <span style={{ color:C.muted, fontSize:10 }}>Know your edge · TradeReflex</span>
        <span style={{ color:C.secondary, fontSize:10, fontWeight:700 }}>tradereflex.in</span>
      </div>
    </div>
  );
}

export default BacktestShareCard;
